import type { School } from '@kimuno/api-sdk/lib/types/responses/Text';
import { Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type Control, Controller, type useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import services from '../../../services';
import SchoolAutocomplete from '../../onboarding/OnboardingStepSchule/SchoolAutocomplete';

interface SchoolAutocompleteFieldProps {
	name: string;
	control: Control;
	setValue: ReturnType<typeof useFormContext>['setValue'];
	isEditMode: boolean;
	defaultValue?: School;
	error?: string;
}

const DEBOUNCE_MS = 300;
const MIN_INPUT_LENGTH = 3;

const SchoolAutocompleteField: React.FC<SchoolAutocompleteFieldProps> = ({
	name,
	control,
	setValue,
	isEditMode,
	defaultValue,
}) => {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState('');
	const [options, setOptions] = useState<School[]>([]);
	const [loading, setLoading] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const school = defaultValue || {
		id: '',
		name: '',
		formattedAddress: '',
		state: '',
	};

	useEffect(() => {
		if (defaultValue?.name && isEditMode) {
			setInputValue(`${defaultValue.name}, ${defaultValue.formattedAddress}`);
		}
	}, [defaultValue, isEditMode]);

	useEffect(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		if (`${defaultValue?.name}, ${defaultValue?.formattedAddress}` === inputValue) return;

		debounceRef.current = setTimeout(async () => {
			if (inputValue.length > MIN_INPUT_LENGTH) {
				setLoading(true);
				try {
					const schools = await services.text.fetchSchools({
						textQuery: inputValue,
					});
					setOptions(schools || []);
				} catch {
					setOptions([]);
				}
				setLoading(false);
			} else {
				setOptions([]);
			}
		}, DEBOUNCE_MS);

		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, [inputValue, defaultValue]);

	const handleSchoolChange = useCallback(
		(selectedSchool: School) => {
			setValue(name, selectedSchool);
			if (selectedSchool?.name) {
				setInputValue(selectedSchool.name);
			}
		},
		[name, setValue]
	);

	if (!isEditMode) {
		return (
			<Typography
				color={defaultValue?.name ? 'text.primary' : 'text.disabled'}
				sx={{
					fontWeight: 400,
					letterSpacing: '0.15px',
				}}
			>
				{defaultValue?.name ? `${defaultValue?.name}, ${defaultValue?.formattedAddress}` : t('Not set')}
			</Typography>
		);
	}

	return (
		<Controller
			name={name}
			control={control}
			defaultValue={school}
			as={
				<SchoolAutocomplete
					value={school}
					inputValue={inputValue}
					options={options}
					loading={loading}
					onInputChange={setInputValue}
					onChange={(selectedSchool) => handleSchoolChange(selectedSchool)}
					placeholder={t('Enter school')}
					noOptionsText={t('No options')}
					loadingText={t('Loading') + '...'}
					setValue={(selectedSchool) => handleSchoolChange(selectedSchool)}
					setInputValue={setInputValue}
				/>
			}
		/>
	);
};

export default React.memo(SchoolAutocompleteField);
